import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';

import * as fileApi from '../../../../lib/api/file';
import * as projectApi from '../../../../lib/api/project';

import {
  initOrder,
  unloadOrderAdd,
} from '../../../../modules/projectModule/orderAdd';
import { listOrderLetters } from '../../../../modules/projectModule/orderLetters';
import { projectOrderCount } from '../../../../modules/projectModule/project';
import {
  listApprovalLine,
  unloadApprovalLine,
} from '../../../../modules/empModule/empApproval';

import OrderBookModalV2Full from '../../../../components/projectmng/obtainorder/v2/OrderBookModalV2Full';
import AskDialog from '../../../../components/common/dialog/AskDialog';

/**
 * 개선된 수주품의서 신규 작성 컨테이너 (V2)
 * 
 * 사용법:
 * <OrderBookAddModalContainerV2
 *   open={open}
 *   handleClose={() => setOpen(false)}
 * />
 */
const OrderBookAddModalContainerV2 = ({ open, handleClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  
  const [apprUuid, setApprUuid] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const scrollRef = useRef();

  // Redux 상태
  const { 
    user, 
    project, 
    receiveOrderInf, 
    billInfList, 
    billInvList,
    approvalLines,
    approvalLoading,
    loading,
  } = useSelector(({ auth, projectModule, empModule, loading }) => ({
    user: auth.user,
    project: projectModule.project.project,
    receiveOrderInf: projectModule.orderAdd.receiveOrderInf,
    billInfList: projectModule.orderAdd.billInfList,
    billInvList: projectModule.orderAdd.billInvList,
    approvalLines: empModule.employeeApproval.approvalLines,
    approvalLoading: loading['employee/LIST_APPROVAL_LINE'],
    loading: loading['orderAdd/INIT_ORDER'],
  }));

  // 초기화
  useEffect(() => {
    if (open && project) {
      dispatch(initOrder(project));
      dispatch(listApprovalLine());
    }

    return () => {
      dispatch(unloadOrderAdd());
      dispatch(unloadApprovalLine());
    };
  }, [open, project, dispatch]);

  // 프로젝트 데이터로 폼 초기화
  useEffect(() => {
    if (project && open) {
      formik.setFieldValue('siDt', project.expDay);
      formik.setFieldValue('dlvryDuedate', dayjs(project.expDay).format('YYYY-MM-DD'));
      formik.setFieldValue('comment', project.forcastTxt || '');
      formik.setFieldValue('roProfitAmt', project.totalProfit || 0);
      formik.setFieldValue('roAdjProfitAmt', project.totalProfit || 0);
      formik.setFieldValue('roProfitAmtPercent', getPercentByTotal(project.totalProfit));
      formik.setFieldValue('roAdjProfitAmtPercent', getPercentByTotal(project.totalProfit));
      
      // 라이센스 기간 기본값
      formik.setFieldValue('licStrtDt', dayjs(project.expDay).format('YYYY-MM-DD'));
      formik.setFieldValue('licEndDt', dayjs(project.expDay).add(12, 'month').endOf('month').format('YYYY-MM-DD'));
      
      // 무상유지보수 기간 (프로젝트에서 가져오기)
      if (project.mtnFreeSt) {
        formik.setFieldValue('mtnFreeStrtDt', project.mtnFreeSt);
      }
      if (project.mtnFreeEd) {
        formik.setFieldValue('mtnFreeEndDt', project.mtnFreeEd);
      }
    }
  }, [project, open]);

  // 권한 확인 (마이너스 이익 입력 권한)
  useEffect(() => {
    const hasAuth = _.findIndex(
      user?.privilegeList,
      (auth) => auth?.authTyp === 'MINUS_PO' && auth?.authYn === 'Y',
    ) !== -1;
    setIsAuth(hasAuth);
  }, [user]);

  // 매출액 대비 퍼센트 계산
  const getPercentByTotal = (value) => {
    const totalSales = project?.totalSales || 0;
    if (totalSales === 0) return 0;
    return Math.round((Number(value) / totalSales) * 100);
  };

  // 폼 초기값
  const initialValues = {
    expDay: new Date(),
    siDt: '',
    dlvryDuedate: '',
    inspectCond: '',
    mtnFreeStrtDt: '',
    mtnFreeEndDt: '',
    licStrtDt: dayjs().format('YYYY-MM-DD'),
    licEndDt: dayjs().add(12, 'month').endOf('month').format('YYYY-MM-DD'),
    collCondt: '세금계산서 발행후 익월말',
    payCondt: '현금',
    giPrePct: '',
    giCovPct: '',
    giMtnPct: '',
    comment: '',
    roProfitAmt: 0,
    roProfitAmtPercent: 0,
    roCostAmt: 0,
    roCostAmtPercent: 0,
    roAdjProfitAmt: 0,
    roAdjProfitAmtPercent: 0,
    roInternalCosts: [
      {
        roPartnerUsrNm: '',
        roPartnerUsr: '',
        roInternalAmt: 0,
        roInternalAmtPercent: 0,
      },
    ],
    files: [{ fileDesc: '', file: null }],
  };

  // 유효성 검증
  const validationSchema = yup.object({
    expDay: yup.date()
      .required('계약일을 입력하세요')
      .nullable(),
    siDt: yup.date()
      .required('계약완료일을 입력하세요')
      .nullable(),
    mtnFreeStrtDt: yup.date()
      .nullable()
      .test('mtnFreeStrtDt-required', '무상 유지보수 시작일을 입력하세요', function(value) {
        return project?.prjTyp !== 'N' || value !== null;
      }),
    mtnFreeEndDt: yup.date()
      .nullable()
      .test('mtnFreeEndDt-required', '무상 유지보수 종료일을 입력하세요', function(value) {
        return project?.prjTyp !== 'N' || value !== null;
      })
      .min(yup.ref('mtnFreeStrtDt'), '종료일은 시작일 이후여야 합니다'),
    collCondt: yup.string().nullable(),
    payCondt: yup.string().nullable(),
    comment: yup.string().nullable(),
    roProfitAmt: yup.number()
      .test('roProfitAmt-min', '최소 0 이상 입력하세요', (value) => isAuth || value >= 0),
    roCostAmt: yup.number()
      .test('roCostAmt-min', '최소 0 이상 입력하세요', (value) => isAuth || value >= 0),
    roInternalCosts: yup.array().of(
      yup.object().shape({
        roPartnerUsrNm: yup.string()
          .when('roInternalAmt', (roInternalAmt, schema) => {
            if (roInternalAmt && roInternalAmt > 0) {
              return schema.required('협업담당자를 선택하세요');
            }
            return schema;
          }),
        roInternalAmt: yup.number()
          .test('roInternalAmt-min', '최소 0 이상 입력하세요', (value) => isAuth || !value || value >= 0),
      }),
    ),
  });

  // Formik
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      // 결재선 확인
      if (!apprUuid) {
        setDialogMessage('결재선이 선택되지 않았습니다. 결재선을 선택하세요.');
        setDialogVisible(true);
        setSubmitting(false);
        return;
      }

      try {
        setSaving(true);
        await handleAddOrder(values);
        handleCloseModal();
      } catch (error) {
        console.error('Submit error:', error);
      } finally {
        setSaving(false);
        setSubmitting(false);
      }
    },
  });

  // 유효성 오류 시 스크롤
  useEffect(() => {
    if (!formik.isValid && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [formik.isValid]);

  // 수주품의서 데이터 생성
  const buildReceiveOrderInf = (values) => ({
    roUuid: receiveOrderInf?.roUuid,
    prjUuid: receiveOrderInf?.prjUuid || project?.prjUuid,
    partnerUuid: receiveOrderInf?.partnerUuid,
    employeeUuid: receiveOrderInf?.employeeUuid,
    partnerInc: receiveOrderInf?.partnerInc,
    chargeNm: receiveOrderInf?.chargeNm,
    salesUuid: receiveOrderInf?.salesUuid,
    
    expDay: values.expDay ? dayjs(values.expDay).format('YYYY-MM-DD') : null,
    siDt: values.siDt ? dayjs(values.siDt).format('YYYY-MM-DD') : null,
    dlvryDuedate: values.dlvryDuedate ? dayjs(values.dlvryDuedate).format('YYYY-MM-DD') : null,
    inspectCond: values.inspectCond,
    
    mtnFreeStrtDt: values.mtnFreeStrtDt ? dayjs(values.mtnFreeStrtDt).format('YYYY-MM-DD') : null,
    mtnFreeEndDt: values.mtnFreeEndDt ? dayjs(values.mtnFreeEndDt).format('YYYY-MM-DD') : null,
    
    licStrtDt: values.licStrtDt ? dayjs(values.licStrtDt).format('YYYY-MM-DD') : null,
    licEndDt: values.licEndDt ? dayjs(values.licEndDt).format('YYYY-MM-DD') : null,
    
    collCondt: values.collCondt,
    payCondt: values.payCondt,
    comment: values.comment,
    
    giPrePct: values.giPrePct,
    giCovPct: values.giCovPct,
    giMtnPct: values.giMtnPct,
    
    roProfitAmt: values.roProfitAmt,
    roProfitAmtPercent: values.roProfitAmtPercent,
    roCostAmt: values.roCostAmt,
    roCostAmtPercent: values.roCostAmtPercent,
    roAdjProfitAmt: values.roAdjProfitAmt,
    roAdjProfitAmtPercent: values.roAdjProfitAmtPercent,
    roInternalCosts: values.roInternalCosts.filter(item => item.roPartnerUsr || item.roInternalAmt > 0),
    roTotalAmt: project?.totalSales,
  });

  // 파일 업로드
  const uploadFile = async (fileItem, orderInfo) => {
    const formData = new FormData();
    formData.append('fileDt', fileItem.file);
    formData.append('fileTyp', 'PROJECT_PO');
    formData.append('fileAttrVal1', orderInfo.prjUuid);
    formData.append('fileAttrVal2', '수주품의서');
    formData.append('fileAttrVal3', orderInfo.roUuid);
    formData.append('fileDesc', fileItem.fileDesc);

    try {
      const response = await fileApi.addFiles(formData);
      if (response.data.result === 'success') {
        enqueueSnackbar('파일 업로드 성공', { variant: 'success' });
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      enqueueSnackbar(`파일 업로드 실패: ${message}`, { variant: 'error' });
    }
  };

  // 신규 등록
  const handleAddOrder = async (values) => {
    const orderLetterInfo = buildReceiveOrderInf(values);
    const dataToSubmit = {
      receiveOrderInf: orderLetterInfo,
      billInfList,
      billInvList,
      apprUuid,
    };

    try {
      const res = await projectApi.addReceiveOrder(dataToSubmit);

      if (res.data) {
        // 파일 업로드
        const filesToUpload = values.files.filter(f => f.file !== null);
        for (const fileItem of filesToUpload) {
          await uploadFile(fileItem, res.data.receiveOrderInf);
        }

        dispatch(listOrderLetters(project.prjUuid));
        dispatch(projectOrderCount(project.prjUuid));

        enqueueSnackbar('수주품의서가 저장되었습니다.', { variant: 'success' });
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      enqueueSnackbar(`저장 실패: ${message}`, { variant: 'error' });
      throw err;
    }
  };

  // 모달 닫기
  const handleCloseModal = () => {
    formik.resetForm();
    setApprUuid(null);
    handleClose();
  };

  // 다이얼로그 핸들러
  const handleDialogConfirm = () => {
    setDialogVisible(false);
  };

  return (
    <>
      <OrderBookModalV2Full
        mode="add"
        open={open}
        handleClose={handleCloseModal}
        project={project}
        orderInfo={receiveOrderInf}
        formik={formik}
        user={user}
        apprUuid={apprUuid}
        setApprUuid={setApprUuid}
        approvalList={null}
        approvalLines={approvalLines}
        approvalLoading={approvalLoading}
        scrollRef={scrollRef}
        loading={loading}
        saving={saving}
      />

      <AskDialog
        visible={dialogVisible}
        title="확인"
        description={dialogMessage}
        confirmText="확인"
        onConfirm={handleDialogConfirm}
        onCancle={handleDialogConfirm}
      />
    </>
  );
};

export default OrderBookAddModalContainerV2;
